import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUserData, getCurrentRole, getDefaultDashboard } from '../auth/authUtils';
import { Button } from '../../components/ui/button';
import { ChevronDown } from 'lucide-react';
import axios from 'axios';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Role Switcher - Allows users with multiple roles to switch between portals
 * Shows: Customer ⇄ Host ⇄ Admin (based on user's roles)
 */
const RoleSwitcher = () => {
  const navigate = useNavigate();
  const userData = getUserData();
  const currentRole = getCurrentRole();
  const [isOpen, setIsOpen] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);

  // Debug logging
  console.log('🔄 RoleSwitcher render:', {
    hasUserData: !!userData,
    roles: userData?.roles || [],
    rolesLength: userData?.roles?.length || 0,
    willShow: userData && userData.roles && userData.roles.length > 1
  });

  if (!userData || !userData.roles || userData.roles.length <= 1) {
    // User only has one role or not logged in, no switcher needed
    console.log('❌ RoleSwitcher hidden - insufficient roles');
    return null;
  }

  const roles = userData.roles;

  const roleConfig = {
    customer: {
      label: '👤 Customer Mode',
      description: 'Book and manage trips',
      path: '/dashboard',
      badge: 'Guest'
    },
    host: {
      label: '🏠 Host Mode',
      description: 'Manage properties',
      path: '/host/dashboard',
      badge: 'Host'
    },
    partner: {
      label: '✨ Partner Mode',
      description: 'Manage your experience listing',
      path: '/partner/dashboard',
      badge: 'Partner'
    },
    admin: {
      label: '🔐 Admin Mode',
      description: 'System management',
      path: '/admin/dashboard',
      badge: 'Admin'
    }
  };

  const switchRole = async (role) => {
    if (isSwitching) return; // Prevent double-clicks
    
    const config = roleConfig[role];
    if (!config) return;
    
    try {
      setIsSwitching(true);
      
      // Get current token
      const token = localStorage.getItem('user_token');
      if (!token) {
        console.error('No token found');
        return;
      }
      
      // Call backend to switch role and get new token
      const response = await axios.post(
        `${API_BASE}/auth/switch-role`,
        { role: role },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.data.success) {
        // Update token and user data in localStorage
        localStorage.setItem('user_token', response.data.token);
        localStorage.setItem('user_data', JSON.stringify(response.data.user));
        localStorage.setItem('currentRole', role);
        
        console.log(`✅ Switched to ${role} mode with new token`);
        
        // Dispatch custom event to notify other components
        window.dispatchEvent(new Event('loginStateChanged'));
        
        // Navigate to appropriate dashboard
        navigate(config.path);
        setIsOpen(false);
      }
    } catch (error) {
      console.error('❌ Failed to switch role:', error);
      
      // Show user-friendly error message
      if (error.response?.data?.detail) {
        alert(`Failed to switch role: ${error.response.data.detail}`);
      } else {
        alert('Failed to switch role. Please try logging in again.');
      }
    } finally {
      setIsSwitching(false);
    }
  };

  const currentConfig = roleConfig[currentRole] || roleConfig[roles[0]]; // Fallback to first role if current is invalid

  console.log('✅ RoleSwitcher will render JSX', { currentRole, currentConfig, availableRoles: roles });

  // Safety check - if currentConfig is still null, don't render
  if (!currentConfig) {
    console.log('❌ RoleSwitcher hidden - no valid config found');
    return null;
  }

  return (
    <div className="relative" data-testid="role-switcher">
      {/* Current Role Badge + Dropdown */}
      <Button
        variant="outline"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 border-2 border-blue-500 bg-blue-50"
        data-testid="role-switcher-button"
        disabled={isSwitching}
      >
        <span className="font-semibold text-blue-700">
          {isSwitching ? 'Switching...' : (currentConfig?.badge || 'Switch Mode')}
        </span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </Button>

      {/* Dropdown Menu */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Menu */}
          <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-gray-200 z-50">
            <div className="p-2">
              <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Switch Mode
              </div>
              
              {roles.map((role) => {
                const config = roleConfig[role];
                if (!config) return null;
                const isActive = role === currentRole;
                
                return (
                  <button
                    key={role}
                    onClick={() => switchRole(role)}
                    disabled={isActive || isSwitching}
                    className={`w-full text-left px-3 py-3 rounded-md transition-colors ${
                      isActive
                        ? 'bg-blue-100 border-2 border-blue-500 cursor-default'
                        : isSwitching
                        ? 'opacity-50 cursor-not-allowed'
                        : 'hover:bg-gray-100 cursor-pointer'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className={`font-medium ${isActive ? 'text-blue-700' : 'text-gray-900'}`}>
                          {config.label}
                        </div>
                        <div className="text-sm text-gray-500">
                          {config.description}
                        </div>
                      </div>
                      {isActive && (
                        <span className="text-blue-600 font-bold">✓</span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default RoleSwitcher;
